import React, { useEffect, useState } from "react";
import { fmtDuration } from "../lib/format";
import { SlaBadge } from "./Badges";
import { Timer, Flag, CircleCheck, PauseCircle } from "lucide-react";

// Live-updating SLA timer widget. Recomputes elapsed/remaining every second
// against the server-provided deadline to stay authoritative on server time.
export default function SlaTimer({ sla, priority }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (sla?.status === "MET" || sla?.status === "BREACHED" || sla?.status === "NOT_STARTED" || sla?.currently_paused) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [sla?.status, sla?.currently_paused]);

  if (!sla) return null;

  const target = sla.target_seconds;
  const deadline = sla.deadline ? new Date(sla.deadline).getTime() : null;
  const now = Date.now();

  let elapsed = sla.elapsed_seconds;
  let remaining = sla.remaining_seconds;
  let status = sla.status;

  const live = !sla.currently_paused && !["MET", "BREACHED", "NOT_STARTED"].includes(sla.status);
  if (live && deadline != null) {
    remaining = (deadline - now) / 1000;
    elapsed = target - remaining;
    if (remaining < 0) status = "BREACHED";
    else if (remaining <= 0.2 * target) status = "AT_RISK";
    else status = "IN_PROGRESS";
  }

  const done = sla.resolution_seconds != null;
  const pct = target ? Math.min(100, Math.max(0, (elapsed / target) * 100)) : 0;
  const barColor =
    status === "BREACHED" ? "bg-red-500" : status === "AT_RISK" ? "bg-amber-500" : status === "MET" ? "bg-emerald-500" : "bg-blue-500";

  return (
    <div data-testid="sla-timer" className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-heading font-bold tracking-tight">
          <Timer className="h-4 w-4 text-primary" /> SLA Monitor
        </div>
        <SlaBadge status={status} />
      </div>

      <div className="grid grid-cols-2 gap-4 text-sm">
        <Metric label="SLA Target" value={fmtDuration(target)} />
        <Metric label={done ? "Resolution" : "Elapsed"} value={fmtDuration(elapsed)} mono />
        {done ? (
          <Metric label="Result" value={status === "MET" ? "Within target" : "Over target"} icon={status === "MET" ? CircleCheck : Flag} />
        ) : (
          <Metric label="Remaining" value={remaining < 0 ? `-${fmtDuration(-remaining)}` : fmtDuration(remaining)} mono danger={remaining < 0} />
        )}
        <Metric label="Paused" value={fmtDuration(sla.paused_seconds)} icon={sla.currently_paused ? PauseCircle : undefined} />
      </div>

      <div className="mt-4">
        <div className="h-2 rounded-full bg-muted overflow-hidden">
          <div className={`h-full ${barColor} transition-all duration-500`} style={{ width: `${pct}%` }} />
        </div>
        {sla.currently_paused && (
          <div className="mt-2 text-xs text-amber-600 dark:text-amber-400 font-medium flex items-center gap-1">
            <PauseCircle className="h-3 w-3" /> SLA clock paused (waiting state)
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, mono, danger, icon: Icon }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</div>
      <div className={`font-semibold flex items-center gap-1.5 ${mono ? "font-mono-plex" : ""} ${danger ? "text-red-600 dark:text-red-400" : ""}`}>
        {Icon && <Icon className="h-3.5 w-3.5" />} {value}
      </div>
    </div>
  );
}
